import { B, fonts } from "../../constants/brand";

// GlobalStyles — a single injected <style> tag for the handful of things that
// inline styles fundamentally cannot express: :focus-visible rings, keyframe
// animations, media queries, and a visually-hidden helper. Mirrors the
// <style>-injection pattern already used in Toast.jsx. Mount once near the app
// root. Everything else stays inline per the project convention.
export const GlobalStyles = () => (
  <style>{`
    /* Base typography — safety net so every element inherits the sans stack
       even if an inline style forgets to set it. */
    html, body, button, input, select, textarea {
      font-family: ${fonts.sans};
    }
    body {
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
      text-rendering: optimizeLegibility;
    }

    /* Selection color — brand-tinted instead of the browser default blue. */
    ::selection {
      background: ${B.accent}33;
      color: ${B.text};
    }

    /* Thin, modern scrollbars (subtle track, rounded thumb). */
    * {
      scrollbar-width: thin;
      scrollbar-color: ${B.textLt}66 transparent;
    }
    *::-webkit-scrollbar {
      width: 10px;
      height: 10px;
    }
    *::-webkit-scrollbar-track {
      background: transparent;
    }
    *::-webkit-scrollbar-thumb {
      background: ${B.textLt}59;
      border-radius: 8px;
      border: 2px solid transparent;
      background-clip: content-box;
    }
    *::-webkit-scrollbar-thumb:hover {
      background: ${B.textMd}80;
      background-clip: content-box;
    }

    /* Keyboard focus ring — only shows for keyboard users, not mouse clicks. */
    a:focus-visible,
    button:focus-visible,
    input:focus-visible,
    select:focus-visible,
    textarea:focus-visible,
    [tabindex]:focus-visible {
      outline: 2px solid ${B.accent};
      outline-offset: 2px;
      border-radius: 6px;
    }

    /* Note editor (BlockNote): the typing area is a contenteditable carrying a
       tabindex, so the ring above — and Safari's own ring for editable text —
       drew a line across the middle of the note box on focus (feedback #588).
       The box keeps its own border; the caret shows it is active. */
    .bn-editor:focus,
    .bn-editor:focus-visible,
    .ProseMirror:focus,
    .ProseMirror:focus-visible {
      outline: none;
      box-shadow: none;
    }

    /* The typing area fills the note box, so a click anywhere in the white
       space lands in the note rather than only on its first line. */
    .harvus-note-body .bn-container,
    .harvus-note-body .bn-editor {
      min-height: var(--harvus-note-min-height, 0);
    }

    /* Spinner animation (consumed by the Spinner primitive). */
    @keyframes harvus-spin {
      to { transform: rotate(360deg); }
    }

    /* Modal / menu entrance animations (consumed by overlay + dialog styles). */
    @keyframes harvus-fade-in {
      from { opacity: 0; }
      to   { opacity: 1; }
    }
    @keyframes harvus-pop-in {
      from { opacity: 0; transform: translateY(8px) scale(0.98); }
      to   { opacity: 1; transform: translateY(0) scale(1); }
    }

    /* Chat transcript — a new turn arrives rather than appearing. */
    @keyframes harvus-msg-in {
      from { opacity: 0; transform: translateY(6px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    /* "Harvi is thinking" — three dots, staggered by inline animation-delay. */
    @keyframes harvus-dot {
      0%, 80%, 100% { opacity: 0.25; transform: translateY(0); }
      40%           { opacity: 1;    transform: translateY(-3px); }
    }
    /* The caret on a streaming answer. */
    @keyframes harvus-blink {
      50% { opacity: 0; }
    }
    /* The thread drawer, on a container too narrow for a persistent rail. */
    @keyframes harvus-slide-in-left {
      from { opacity: 0; transform: translateX(-12px); }
      to   { opacity: 1; transform: translateX(0); }
    }

    /* Visually-hidden text for screen-reader-only labels. */
    .harvus-sr-only {
      position: absolute;
      width: 1px;
      height: 1px;
      padding: 0;
      margin: -1px;
      overflow: hidden;
      clip: rect(0, 0, 0, 0);
      white-space: nowrap;
      border: 0;
    }

    /* Skip-to-content link — off-screen until focused. */
    .harvus-skip-link {
      position: absolute;
      left: 8px;
      top: -48px;
      z-index: 10000;
      background: ${B.navy};
      color: ${B.white};
      padding: 10px 16px;
      border-radius: 6px;
      font-size: 13px;
      font-weight: 600;
      text-decoration: none;
      transition: top 0.15s ease-out;
    }
    .harvus-skip-link:focus {
      top: 8px;
    }

    /* Lodge choice cards (pages/outfitter/components/LodgeUI.jsx). */
    .lodge-card:hover:not(:disabled) { border-color: ${B.navy} !important; box-shadow: ${B.shadowSm}; }
    .lodge-card[aria-pressed="true"]:hover, .lodge-card[aria-checked="true"]:hover { box-shadow: 0 0 0 1px ${B.navy} inset, ${B.shadowSm}; }
    .lodge-card:focus-visible, .lodge-seg:focus-visible { outline: 2px solid ${B.accent}; outline-offset: 2px; }
    .lodge-switch:focus-visible + span { outline: 2px solid ${B.accent}; outline-offset: 2px; }
    .lodge-seg[aria-pressed="false"]:hover { color: ${B.text} !important; }

    /* A Lodge document / waiver body (components/DocBody.jsx) — the formatting
       the rich-text editor writes, kept compact inside a reading box. */
    .lodge-doc-body > :first-child { margin-top: 0; }
    .lodge-doc-body > :last-child { margin-bottom: 0; }
    .lodge-doc-body p { margin: 0 0 0.7em; }
    .lodge-doc-body h1, .lodge-doc-body h2, .lodge-doc-body h3, .lodge-doc-body h4 { margin: 0.9em 0 0.4em; line-height: 1.3; }
    .lodge-doc-body h1 { font-size: 1.35em; } .lodge-doc-body h2 { font-size: 1.2em; } .lodge-doc-body h3, .lodge-doc-body h4 { font-size: 1.05em; }
    .lodge-doc-body ul, .lodge-doc-body ol { margin: 0 0 0.7em; padding-left: 1.4em; }
    .lodge-doc-body li { margin: 0.15em 0; }
    .lodge-doc-body blockquote { margin: 0 0 0.7em; padding-left: 0.8em; border-left: 3px solid ${B.border}; color: ${B.textMd}; }
    .lodge-doc-body table { border-collapse: collapse; margin: 0 0 0.7em; }
    .lodge-doc-body th, .lodge-doc-body td { border: 1px solid ${B.border}; padding: 4px 8px; text-align: left; vertical-align: top; }
    .lodge-doc-body a { color: ${B.navy}; }

    /* Responsive helpers — inline styles can't do media queries. */
    @media (max-width: 768px) {
      /* Multi-column form/control grids collapse to a single column. */
      .harvus-responsive-grid {
        grid-template-columns: 1fr !important;
      }
      /* Primary touch targets get a comfortable minimum height. */
      .harvus-touch-target {
        min-height: 44px;
      }
    }

    /* Respect users who prefer reduced motion. */
    @media (prefers-reduced-motion: reduce) {
      *, *::before, *::after {
        animation-duration: 0.01ms !important;
        animation-iteration-count: 1 !important;
        transition-duration: 0.01ms !important;
      }
    }
  `}</style>
);
